import React from 'react';
import { useDeleteBackgroundControl } from '../../../../../../../../traits/api/backgroundControls/useDeleteBackgroundControl';
import { useDeleteBackgroundControlFile } from '../../../../../../../../traits/api/backgroundControls/useDeleteBackgroundControlFile';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog/ConfirmDeleteDialog';
import { S } from './BackgroundControlFilesTable.styles';

type Control = {
  backgroundControlId: string;
  createdAt: string;
  reportFiles: Array<{
    backgroundControlFileId: string;
    fileName: string;
    file: { originalName: string };
  }>;
};

type Props = {
  controls: Control[];
};

/**
 * One row per report file (file name, date, delete). A control without any
 * file gets a single row so it can still be removed.
 */
export const BackgroundControlFilesTable: React.FC<Props> = ({ controls }) => {
  const deleteBgControl = useDeleteBackgroundControl();
  const deleteBgFile = useDeleteBackgroundControlFile();
  const [fileToDelete, setFileToDelete] = React.useState<{
    backgroundControlFileId: string;
    fileName: string;
    isLastFile: boolean;
  } | null>(null);
  const [controlToDelete, setControlToDelete] = React.useState<{
    backgroundControlId: string;
    date: string;
  } | null>(null);

  return (
    <>
      <S.Table>
        <thead>
          <tr>
            <S.Th>Filnamn</S.Th>
            <S.Th>Datum</S.Th>
            <S.Th />
          </tr>
        </thead>
        <tbody>
          {controls.flatMap((control) => {
            const date = new Date(control.createdAt)
              .toISOString()
              .split('T')[0];

            if (control.reportFiles.length === 0) {
              return (
                <tr key={control.backgroundControlId}>
                  <S.Td>–</S.Td>
                  <S.Td>{date}</S.Td>
                  <S.Td>
                    <S.IconButton
                      type="button"
                      aria-label={`Radera bakgrundskontrollen från ${date}`}
                      onClick={() =>
                        setControlToDelete({
                          backgroundControlId: control.backgroundControlId,
                          date,
                        })
                      }
                    >
                      <S.Icon src="Trash" height={20} />
                    </S.IconButton>
                  </S.Td>
                </tr>
              );
            }

            return control.reportFiles.map((reportFile) => {
              const fileName =
                reportFile.fileName || reportFile.file.originalName;

              return (
                <tr key={reportFile.backgroundControlFileId}>
                  <S.Td>{fileName}</S.Td>
                  <S.Td>{date}</S.Td>
                  <S.Td>
                    <S.IconButton
                      type="button"
                      aria-label={`Radera filen ${fileName}`}
                      onClick={() =>
                        setFileToDelete({
                          backgroundControlFileId:
                            reportFile.backgroundControlFileId,
                          fileName,
                          isLastFile: control.reportFiles.length === 1,
                        })
                      }
                    >
                      <S.Icon src="Trash" height={20} />
                    </S.IconButton>
                  </S.Td>
                </tr>
              );
            });
          })}
        </tbody>
      </S.Table>

      <ConfirmDeleteDialog
        isOpen={fileToDelete !== null}
        title={`Ta bort filen ${fileToDelete?.fileName ?? ''}?`}
        description={
          fileToDelete?.isLastFile
            ? 'Detta är sista filen – bakgrundskontrollen tas också bort. Detta kan inte ångras.'
            : 'Detta kan inte ångras.'
        }
        isLoading={deleteBgFile.isLoading}
        onClose={() => setFileToDelete(null)}
        onConfirm={() => {
          if (!fileToDelete) return;
          deleteBgFile.mutate(fileToDelete.backgroundControlFileId, {
            onSettled: () => setFileToDelete(null),
          });
        }}
      />

      <ConfirmDeleteDialog
        isOpen={controlToDelete !== null}
        title={`Ta bort bakgrundskontrollen från ${
          controlToDelete?.date ?? ''
        }?`}
        description="Bakgrundskontrollen raderas. Detta kan inte ångras."
        isLoading={deleteBgControl.isLoading}
        onClose={() => setControlToDelete(null)}
        onConfirm={() => {
          if (!controlToDelete) return;
          deleteBgControl.mutate(controlToDelete.backgroundControlId, {
            onSettled: () => setControlToDelete(null),
          });
        }}
      />
    </>
  );
};
